import AngleLeft from "@icons/angle-left.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import React from "react";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";
import { z } from "zod";

import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { BindingOfferOrderAllTradersDialog } from "@/common/components/binding-offer-dialog/binding-offer-order-all-traders-dialog";
import { BindingOfferOrderDialog } from "@/common/components/binding-offer-dialog/binding-offer-order-dialog";
import { Button } from "@/common/components/button/button";
import { trackTradingOrderClick } from "@/common/components/mixpanel/mixpanel-tracking";
import { Fields, OrderForm } from "@/common/components/order-form/order-form";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { useFormConfirmation } from "@/common/hooks/use-form-confirmation";
import { entitySchema } from "@/common/schema/entity";
import { currencyFormatter } from "@/common/services/formatter";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";
import { futureQuartersQuery } from "@/routes/trades/trades.api";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { usePostalCodesQuery } from "../shared/api/get-postal-codes";
import { usePostalCodeDialogParams } from "../shared/components/postal-code-dialog/postal-code-dialog";
import { useGoToFailed } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import { useAddFastOrderMutation } from "./quick-trading.detail.api";

const path = "order";
const Order = () => {
  const { id: companyId } = useRequiredErpCompany();
  const context = useOrderContext();
  const addFastOrder = useAddFastOrderMutation();
  const [, currency] = useCurrency();
  const navigate = useNavigate();
  const postalCodes = useQuery(usePostalCodesQuery());
  const { params } = usePostalCodeDialogParams();
  const [searchParams] = useSearchParams();
  const postalCodeObject = postalCodes.data?.find(
    (postalCode) => postalCode.code === params?.postalCode,
  );
  const goToFailedScreen = useGoToFailed();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(postalCodeObject, "Postal code is not set");
  const { confirm, dialog, formData, rejectRef, resolveRef } =
    useFormConfirmation<Fields>();
  // form gives quarter ID only; need name for dialog — deduped with OrderForm.QuarterField
  const quarters = useSuspenseQuery(futureQuartersQuery());

  const handleSubmit: React.ComponentProps<
    typeof OrderForm<Fields>
  >["onSubmit"] = (data) => {
    trackTradingOrderClick({
      crop: context.crop.name,
      currency,
      price: data.pricePerTon,
      quarter: context.quarter.name,
    });
    addFastOrder.mutate(
      {
        amount: data.tons,
        companyId,
        cropId: context.crop.id,
        currency: currency as Currency,
        gps: {
          latitude: context.postalCodeObject.latitude,
          longitude: context.postalCodeObject.longitude,
        },
        harvestYear: data.harvestYear,
        lowPrice: data.lowPricePerTon,
        note: data.note,
        price: data.pricePerTon,
        quarterId: data.quarter,
        zip: postalCodeObject.code,
        ...(data.trader === "ALL" ? {} : { onlyTrader: data.trader }),
      },
      {
        onError: goToFailedScreen,
        onSuccess: async (data) => {
          await navigate(
            `${data.addFastOrder?.id}/offer/${data.addFastOrder?.offer?.id}/${successPath}?${searchParams.toString()}`,
          );
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  const handleFormSubmit = (data: Fields) => {
    confirm(
      <Trans>Přejete si nastavit příkaz k prodeji?</Trans>,
      data,
      guardedSubmit,
    );
  };

  const postalCodeLabel = context.postalCodeObject.label;
  const selectedTrader =
    formData?.trader && formData.trader !== "ALL"
      ? context.traders.find((t) => t.id === formData.trader)
      : undefined;
  const selectedQuarterName =
    quarters.data.find((q) => q.id === formData?.quarter)?.name ??
    context.quarter.name;

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to="..">
            <AngleLeft />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Nastavit příkaz k prodeji</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Komodita</Trans>,
              value: context.crop.name,
            },
            {
              label: <Trans>PSČ</Trans>,
              value: postalCodeLabel,
            },
          ]}
        />
        <Spacer className="h-6" />
        <OrderForm<Fields>
          initialValues={{
            pricePerTon: context.pricePerTon,
            quarter: context.quarter.id,
            tons: context.amount,
            trader: context.traderId,
          }}
          onSubmit={handleFormSubmit}
        >
          <OrderForm.QuarterField />
          <OrderForm.LowPriceField currency={currency} />
          <OrderForm.PriceField
            currency={currency}
            validate={(value) =>
              value > context.pricePerTon ||
              t`Cena musí být vyšší než aktuální nabídka`
            }
          />
          <OrderForm.TraderField>
            <option value="ALL">
              <Trans>Všichni obchodníci</Trans>
            </option>
            {context.traders.map((trader) => (
              <option key={trader.id} value={trader.id}>
                {trader.name}
              </option>
            ))}
          </OrderForm.TraderField>
          <OrderForm.AmountField />
          <OrderForm.NoteField />
        </OrderForm>
        <Button.Container>
          <Button form={OrderForm.formId} type="submit" variant="primary">
            <Trans>Nastavit příkaz</Trans>
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      <Outlet />
      {dialog && formData ? (
        formData.trader === "ALL" ? (
          <BindingOfferOrderAllTradersDialog
            amount={formData.tons + " t"}
            cropName={context.crop.name}
            isPending={addFastOrder.status === "pending"}
            onCancel={() => rejectRef.current?.()}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={postalCodeLabel}
            quarter={selectedQuarterName}
            unitPrice={currencyFormatter(currency).format(formData.pricePerTon)}
          />
        ) : (
          <BindingOfferOrderDialog
            amount={formData.tons + " t"}
            cropName={context.crop.name}
            isPending={addFastOrder.status === "pending"}
            onCancel={() => rejectRef.current?.()}
            onConfirm={(e) => resolveRef.current?.(e)}
            postalCodeLabel={postalCodeLabel}
            quarter={selectedQuarterName}
            timeFromTrading={selectedTrader?.timeFromTrading ?? null}
            timeToTrading={selectedTrader?.timeToTrading ?? null}
            tocUrl={selectedTrader?.tocUrl ?? null}
            traderName={selectedTrader?.name ?? ""}
            unitPrice={currencyFormatter(currency).format(formData.pricePerTon)}
          />
        )
      ) : null}
    </>
  );
};

const contextSchema = z.object({
  amount: z.number().or(z.nan()).optional(),
  crop: entitySchema,
  postalCodeObject: z.object({
    code: z.string(),
    label: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    name: z.string(),
  }),
  pricePerTon: z.number(),
  quarter: entitySchema,
  traderId: z.string(),
  traders: z.array(traderSchema),
});

const useOrderContext = () => {
  const context = useOutletContext();

  return contextSchema.parse(context);
};

export { Order, path };
